import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, MessageCircle, Share2, Bookmark, Eye, Music, ImageIcon } from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import DeviceFramePreview, {
  type DeviceType,
} from '../common/DeviceFramePreview';
import type { Platform, MediaFile, UrlMedia, LinkPreview } from './types';
import { PlatformMediaGrid } from './PlatformMediaGrid';

/**
 * Per-platform device availability. Mobile-only platforms (TikTok,
 * Instagram) hide the Tablet/Desktop tabs so the preview doesn't
 * show a layout the platform never actually renders to users. Text
 * platforms (Twitter/X, LinkedIn, Facebook) get the full three
 * devices so desktop web feed can be previewed.
 */
const PLATFORM_DEVICES: Record<string, DeviceType[]> = {
  instagram: ['phone'],
  tiktok: ['phone'],
  youtube: ['phone', 'tablet', 'desktop'],
  twitter: ['phone', 'tablet', 'desktop'],
  linkedin: ['phone', 'tablet', 'desktop'],
  facebook: ['phone', 'tablet', 'desktop'],
  pinterest: ['phone', 'tablet', 'desktop'],
  bluesky: ['phone', 'tablet', 'desktop'],
};

/** URL-bar hostname for each platform. Shows inside the device frame. */
const PLATFORM_URL_HOST: Record<string, string> = {
  instagram: 'instagram.com',
  tiktok: 'tiktok.com',
  twitter: 'twitter.com',
  facebook: 'facebook.com',
  linkedin: 'linkedin.com',
  youtube: 'youtube.com',
  pinterest: 'pinterest.com',
  bluesky: 'bsky.app',
};

/** Reusable carousel overlay: prev/next arrows + dot indicators */
const CarouselOverlay: React.FC<{
  total: number;
  current: number;
  onPrev: () => void;
  onNext: () => void;
  light?: boolean; // true = white dots for dark backgrounds (TikTok)
}> = ({ total, current, onPrev, onNext, light = false }) => {
  const { t } = useTranslation();
  if (total <= 1) { return null; }
  const dotActive = light ? 'bg-white w-2' : 'bg-blue-500 w-2';
  const dotInactive = light ? 'bg-white/50' : 'bg-white/70';
  return (
    <>
      {current > 0 && (
        <button onClick={onPrev} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-700 shadow-sm hover:bg-white transition" aria-label={t('comp_prev', 'Previous')}>‹</button>
      )}
      {current < total - 1 && (
        <button onClick={onNext} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-700 shadow-sm hover:bg-white transition" aria-label={t('comp_next', 'Next')}>›</button>
      )}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? dotActive : dotInactive}`} />
        ))}
      </div>
    </>
  );
};

/** Fallback placeholder when an image fails to load */
const MediaFallback: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center ${className}`}>
    <ImageIcon className="w-8 h-8 text-gray-300" />
  </div>
);

/** Img wrapper that swaps to fallback on error */
const SafeImg: React.FC<{ src: string; alt?: string; className?: string; fallbackClass?: string }> = ({ src, alt = '', className = '', fallbackClass }) => {
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);
  if (failed || !src) { return <MediaFallback className={fallbackClass || className} />; }
  return <img src={src} alt={alt} className={className} onError={handleError} />;
};

interface Props {
  selectedPlatforms: string[];
  platforms: Platform[];
  caption: string;
  mediaFiles: MediaFile[];
  urlMediaItems?: UrlMedia[];
  linkPreview: LinkPreview | null;
  connectedAccounts: { id: string; platform: string; display_name?: string; avatar_url?: string; username?: string }[];
  selectedAccounts: string[];
}

export const PostPreview: React.FC<Props> = ({
  selectedPlatforms,
  platforms,
  caption,
  mediaFiles,
  urlMediaItems = [],
  linkPreview: _linkPreview,
  connectedAccounts,
  selectedAccounts,
}) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();
  const [device, setDevice] = useState<DeviceType>('phone');
  const [activePlatform, setActivePlatform] = useState<string | null>(null);
  const [carouselIdx, setCarouselIdx] = useState(0);

  // Default to first platform if none selected
  const currentPlatformId = activePlatform || selectedPlatforms[0];
  const platform = platforms.find((p) => p.id === currentPlatformId);

  // Per-platform device availability. If the current platform is
  // mobile-only (TikTok, Instagram) and ``device`` is still
  // ``'tablet'``/``'desktop'`` from a previous selection, snap to
  // phone so the preview renders something valid.
  const availableDevices: DeviceType[] =
    PLATFORM_DEVICES[currentPlatformId || ''] ||
    ['phone', 'tablet', 'desktop'];
  const effectiveDevice: DeviceType = availableDevices.includes(device)
    ? device
    : 'phone';

  // Collect all media URLs
  const allMediaUrls: string[] = [
    ...mediaFiles.map((f) => f.preview).filter(Boolean),
    ...urlMediaItems.filter((m) => m.status === 'done').map((m) => m.url),
  ] as string[];
  const firstMediaUrl = allMediaUrls[0] || null;
  const activeMediaUrl = allMediaUrls[Math.min(carouselIdx, allMediaUrls.length - 1)] || firstMediaUrl;

  if (selectedPlatforms.length === 0) { return null; }

  const displayCaption = platform ? caption.substring(0, platform.maxChars) : caption;
  const isOverLimit = platform ? caption.length > platform.maxChars : false;

  const selectedAccount = connectedAccounts.find(
    (a) => selectedAccounts.includes(a.id) && a.platform === currentPlatformId
  );

  const renderPlatformPreview = () => {
    const username = selectedAccount?.username || 'your_account';
    const displayName = selectedAccount?.username || 'Your Account';
    const avatarUrl = selectedAccount?.avatar_url;

    switch (currentPlatformId) {
      case 'instagram':
        return (
          <div className="bg-white">
            {/* Instagram Header */}
            <div className="px-3 py-2.5 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-yellow-400 to-pink-600" />
                )}
                <p className="text-xs font-semibold text-gray-900">{username}</p>
              </div>
              <span className="text-gray-400">⋯</span>
            </div>

            {/* Image / Carousel */}
            {allMediaUrls.length > 0 ? (
              <div className="relative">
                <SafeImg src={activeMediaUrl || allMediaUrls[0]} className="w-full aspect-square object-cover" fallbackClass="w-full aspect-square" />
                <CarouselOverlay total={allMediaUrls.length} current={carouselIdx} onPrev={() => setCarouselIdx((i) => i - 1)} onNext={() => setCarouselIdx((i) => i + 1)} />
                {allMediaUrls.length > 1 && (
                  <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    {carouselIdx + 1}/{allMediaUrls.length}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                <span className="text-gray-300 text-sm">{t('compose_no_image', 'No image')}</span>
              </div>
            )}

            {/* Action Icons */}
            <div className="px-3 py-2 flex items-center gap-3">
              <Heart className="h-5 w-5 text-gray-900" />
              <MessageCircle className="h-5 w-5 text-gray-900" />
              <Share2 className="h-5 w-5 text-gray-900" />
              <Bookmark className="h-5 w-5 text-gray-900 ml-auto" />
            </div>

            {/* Stats */}
            <div className="px-3 py-1 text-xs font-medium text-gray-900">{t('compose_liked_by_others', 'Liked by 0 others')}</div>

            {/* Caption */}
            <div className="px-3 py-2 text-[11px] leading-relaxed">
              <span className="font-semibold">{username}</span>{' '}
              <span className="text-gray-800 whitespace-pre-wrap">{displayCaption || t('compose_no_caption', 'No caption')}</span>
            </div>

            {/* Comments Link */}
            <div className="px-3 py-1 text-[10px] text-gray-400">{t('compose_view_all_comments', 'View all 0 comments')}</div>

            {/* Timestamp */}
            <div className="px-3 py-1.5 text-[9px] text-gray-400 uppercase">{t('compose_two_hours_ago', '2 hours ago')}</div>
          </div>
        );

      case 'tiktok':
        return (
          <div className="bg-black text-white">
            {/* TikTok Video Area */}
            <div className="relative bg-black w-full aspect-[9/16] flex items-center justify-center">
              {firstMediaUrl ? (
                <>
                  <SafeImg src={activeMediaUrl || firstMediaUrl || ''} className="w-full h-full object-cover" fallbackClass="w-full h-full" />
                  <CarouselOverlay total={allMediaUrls.length} current={carouselIdx} onPrev={() => setCarouselIdx((i) => i - 1)} onNext={() => setCarouselIdx((i) => i + 1)} light />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-b from-gray-800 to-black flex items-center justify-center">
                  <span className="text-gray-500 text-sm">{t('compose_no_video', 'No video')}</span>
                </div>
              )}

              {/* Right Sidebar Icons */}
              <div className="absolute right-3 bottom-20 flex flex-col items-center gap-4">
                {/* Profile */}
                <div className="flex flex-col items-center gap-1">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full border-2 border-white" />
                  ) : (
                    <div className="h-8 w-8 rounded-full border-2 border-white bg-gray-600" />
                  )}
                  <span className="text-[8px] text-gray-300">+</span>
                </div>

                {/* Heart */}
                <div className="flex flex-col items-center">
                  <Heart className="h-6 w-6 text-white fill-white" />
                  <span className="text-[8px] text-white mt-0.5">0</span>
                </div>

                {/* Comment */}
                <div className="flex flex-col items-center">
                  <MessageCircle className="h-6 w-6 text-white" />
                  <span className="text-[8px] text-white mt-0.5">0</span>
                </div>

                {/* Bookmark */}
                <Bookmark className="h-6 w-6 text-white" />

                {/* Share */}
                <Share2 className="h-6 w-6 text-white" />
              </div>

              {/* Bottom Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/50 to-transparent p-3 text-white">
                <p className="text-xs font-semibold">@{username}</p>
                <p className="text-[10px] mt-1 line-clamp-2 whitespace-pre-wrap">{displayCaption || t('compose_no_caption', 'No caption')}</p>
                <div className="flex items-center gap-1 text-[9px] mt-1.5 text-gray-300">
                  <Music className="h-3 w-3" />
                  {t('compose_original_sound', 'Original sound')}
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex justify-between items-center px-3 py-2 text-[8px] text-gray-400 border-t border-gray-700">
              <span>{t('compose_tiktok_home', 'Home')}</span>
              <span>{t('compose_tiktok_discover', 'Discover')}</span>
              <span className="text-white text-xs">+</span>
              <span>{t('compose_tiktok_inbox', 'Inbox')}</span>
              <span>{t('compose_tiktok_profile', 'Profile')}</span>
            </div>
          </div>
        );

      case 'twitter':
        return (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Twitter Header */}
            <div className="px-3 py-2.5 flex gap-2.5">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-300" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-bold text-gray-900">{displayName}</p>
                  <span className="text-xs text-gray-500">@{username}</span>
                  <span className="text-xs text-gray-500">• 2h</span>
                </div>
              </div>
            </div>

            {/* Caption */}
            <div className="px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap">
              {displayCaption || t('compose_no_content', 'No content')}
            </div>

            {/* Image Grid */}
            {allMediaUrls.length > 0 && (
              <div className="px-3 py-2">
                <PlatformMediaGrid platform="twitter" urls={allMediaUrls} />
              </div>
            )}

            {/* Action Bar */}
            <div className="px-3 py-2 flex justify-between text-xs text-gray-500 border-t border-gray-100">
              <div className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4" />
                <span>0</span>
              </div>
              <div className="flex items-center gap-1">
                <Share2 className="h-4 w-4" />
                <span>0</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4" />
                <span>0</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span>0</span>
              </div>
              <div className="flex items-center gap-1">
                <Bookmark className="h-4 w-4" />
              </div>
            </div>
          </div>
        );

      case 'youtube':
        return (
          <div className="bg-white space-y-2">
            {/* Thumbnail */}
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              {firstMediaUrl ? (
                <>
                  <SafeImg src={activeMediaUrl || firstMediaUrl || ''} className="w-full h-full object-cover" fallbackClass="w-full h-full" />
                  <CarouselOverlay total={allMediaUrls.length} current={carouselIdx} onPrev={() => setCarouselIdx((i) => i - 1)} onNext={() => setCarouselIdx((i) => i + 1)} />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-black flex items-center justify-center">
                  <span className="text-4xl text-gray-500">▶</span>
                </div>
              )}
              <div className="absolute bottom-2 right-2 bg-black text-white text-xs px-1.5 py-0.5 rounded">
                0:00
              </div>
            </div>

            {/* Title and Metadata */}
            <div className="space-y-2">
              <p className="font-bold text-sm text-gray-900 line-clamp-2">
                {displayCaption || t('compose_video_title', 'Video Title')}
              </p>
              <div className="flex items-center gap-2.5">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gray-300" />
                )}
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-gray-500">{t('compose_youtube_views', '0 views • 2 hours ago')}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'linkedin':
        return (
          <div className="bg-white border border-gray-300 rounded-lg overflow-hidden space-y-2">
            {/* Header */}
            <div className="px-3 pt-3 flex items-center gap-2.5">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-300" />
              )}
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                <p className="text-xs text-gray-500">Content Creator • 2 hours ago</p>
              </div>
            </div>

            {/* Caption */}
            <div className="px-3 text-sm text-gray-900 whitespace-pre-wrap">
              {displayCaption || t('compose_no_content', 'No content')}
            </div>

            {/* Image Grid */}
            {allMediaUrls.length > 0 && (
              <PlatformMediaGrid platform="linkedin" urls={allMediaUrls} />
            )}

            {/* Reaction Bar */}
            <div className="px-3 py-2 flex gap-4 border-t border-gray-200 text-xs text-gray-500">
              <button className="hover:text-blue-600 transition flex items-center gap-1">
                <Heart className="h-4 w-4" />
                {t('compose_linkedin_like', 'Like')}
              </button>
              <button className="hover:text-blue-600 transition flex items-center gap-1">
                <MessageCircle className="h-4 w-4" />
                {t('compose_linkedin_comment', 'Comment')}
              </button>
              <button className="hover:text-blue-600 transition flex items-center gap-1">
                <Share2 className="h-4 w-4" />
                {t('compose_linkedin_repost', 'Repost')}
              </button>
              <button className="hover:text-blue-600 transition ml-auto flex items-center gap-1">
                <Share2 className="h-4 w-4" />
                {t('compose_linkedin_send', 'Send')}
              </button>
            </div>
          </div>
        );

      case 'facebook':
        return (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Facebook Header */}
            <div className="px-3 py-2.5 flex items-center gap-2.5">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">
                  {(displayName || 'P')[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                <p className="text-xs text-gray-500">2h • 🌐</p>
              </div>
              <span className="text-gray-400 text-lg">⋯</span>
            </div>

            {/* Caption */}
            {displayCaption && (
              <div className="px-3 pb-2 text-sm text-gray-900 whitespace-pre-wrap">
                {displayCaption}
              </div>
            )}

            {/* Image Grid */}
            {allMediaUrls.length > 0 && (
              <PlatformMediaGrid platform="facebook" urls={allMediaUrls} />
            )}

            {/* Reaction Bar */}
            <div className="px-3 py-2 flex justify-between items-center text-xs text-gray-500 border-t border-gray-200">
              <div className="flex items-center gap-1">
                <span className="flex -space-x-0.5">
                  <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-[8px] text-white">👍</span>
                  <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center text-[8px] text-white">❤️</span>
                </span>
                <span>0</span>
              </div>
              <span>0 {t('compose_fb_comments', 'comments')}</span>
            </div>
            <div className="px-3 py-1.5 flex gap-1 border-t border-gray-200 text-xs text-gray-600 font-medium">
              <button className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-gray-100 transition">
                <Heart className="h-4 w-4" /> {t('compose_fb_like', 'Like')}
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-gray-100 transition">
                <MessageCircle className="h-4 w-4" /> {t('compose_fb_comment', 'Comment')}
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-gray-100 transition">
                <Share2 className="h-4 w-4" /> {t('compose_fb_share', 'Share')}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-white px-3 py-2 text-sm text-gray-600">
            {allMediaUrls.length > 0 && (
              <PlatformMediaGrid platform="facebook" urls={allMediaUrls} className="mb-2" />
            )}
            <p className="text-[11px] text-gray-800 whitespace-pre-wrap">
              {displayCaption || 'No content'}
            </p>
          </div>
        );
    }
  };

  // Caption char counter — lives below the device as the outer footer
  // so it mirrors the "Made with PlugD" slot in the page builder.
  const charCounter = platform ? (
    <span className={isOverLimit ? 'text-red-500' : undefined}>
      {caption.length}/{platform.maxChars}{' '}
      {t('compose_characters', 'characters')}
    </span>
  ) : undefined;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 space-y-3">
        {/* Platform tabs — selects which platform's feed card to render
            inside the shared device frame. */}
        <div className="flex gap-1 flex-wrap">
          {selectedPlatforms.map((pid) => {
            const p = platforms.find((x) => x.id === pid);
            if (!p) { return null; }
            return (
              <button
                key={pid}
                type="button"
                onClick={() => {
                  setActivePlatform(pid);
                  setCarouselIdx(0);
                }}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  currentPlatformId === pid
                    ? 'text-white'
                    : 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                }`}
                style={
                  currentPlatformId === pid
                    ? { backgroundColor: theme.hex.primary }
                    : undefined
                }
              >
                {p.icon} {p.name}
              </button>
            );
          })}
        </div>

        {/* Unified device frame — same Phone / Tablet / Desktop chrome
            the page builder uses. Platform-specific feed card lives
            inside via ``renderPlatformPreview()``. */}
        <DeviceFramePreview
          device={effectiveDevice}
          onDeviceChange={setDevice}
          availableDevices={availableDevices}
          urlText={
            currentPlatformId
              ? `${PLATFORM_URL_HOST[currentPlatformId] || 'plugd.io'}`
              : undefined
          }
          outerFooter={charCounter}
        >
          {renderPlatformPreview()}
        </DeviceFramePreview>
      </div>
    </div>
  );
};
